import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { MemberNewsletterCard } from '../components/MemberNewsletterCard';
import { toast } from 'sonner';
import { 
  Mail, Users, RefreshCw, Shield, LogOut, Search, Filter, 
  Check, X, AlertCircle, AlertTriangle, Clock, Link as LinkIcon, Upload, Download,
  BarChart3, UserPlus, Trash2, Edit2
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';

const API_URL = "";

const MEMBER_TYPES = [
  { value: 'student', label: 'Student' },
  { value: 'parent', label: 'Parent' },
  { value: 'donor', label: 'Donor' },
  { value: 'member', label: 'Member' },
  { value: 'alumni', label: 'Alumni' },
  { value: 'general', label: 'General' },
];

const PROGRAM_INTERESTS = [
  { value: 'kids_classes', label: 'Kids Classes' },
  { value: 'adult_classes', label: 'Adult Classes' },
  { value: 'workshops', label: 'Workshops' },
  { value: 'events', label: 'Events' },
];

const SYNC_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  synced: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  none: 'bg-gray-100 text-gray-600',
};

const AdminSubscribers = () => {
  const { token, isAdmin, isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [ccConnected, setCcConnected] = useState(false);
  const [ccStatus, setCcStatus] = useState('disconnected'); // 'connected' | 'expired' | 'disconnected'
  const [ccDetails, setCcDetails] = useState(null);
  const [oauthDiag, setOauthDiag] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    member_type: '',
    program_interest: '',
    newsletter_opt_in: '',
    sync_status: '',
    search: ''
  });
  
  // Edit modal
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [resettingErrors, setResettingErrors] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);  // { type, payload }
  const [deleteSubscriberTarget, setDeleteSubscriberTarget] = useState(null);

  useEffect(() => {
    if (token && isAdmin) {
      fetchData();
      checkCCConnection();
      
      // Check for OAuth callback result
      if (searchParams.get('connected') === 'true') {
        toast.success('Successfully connected to Constant Contact!');
      } else if (searchParams.get('error')) {
        const reason = searchParams.get('reason') || searchParams.get('error');
        toast.error(`Constant Contact connection failed: ${reason}`, { duration: 12000 });
        // Show the setup panel so the cause is visible immediately
        runOAuthDiagnostics({ silent: true });
      }
    }
  }, [token, isAdmin, searchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/newsletter/subscribers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/newsletter/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (subsRes.ok) {
        setSubscribers(await subsRes.json());
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load subscriber data');
    } finally {
      setLoading(false);
    }
  };

  const checkCCConnection = async () => {
    try {
      // First check basic auth status
      const response = await fetch(`${API_URL}/api/newsletter/auth/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (!data.connected) {
          setCcConnected(false);
          setCcStatus('disconnected');
          return;
        }

        // Connected at the OAuth-token-record level; verify token validity
        const statusRes = await fetch(`${API_URL}/api/newsletter/cc-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setCcDetails(statusData);
          if (statusData.status === 'expired' || statusData.status === 'needs_reauth') {
            // Distinct state — neither "connected" nor a fresh "never connected"
            setCcConnected(false);
            setCcStatus('expired');
            toast.warning(statusData.message || 'Connection expired — please reconnect Constant Contact.');
          } else if (statusData.status === 'refreshed') {
            setCcConnected(true);
            setCcStatus('connected');
            toast.success('Constant Contact token was automatically refreshed.');
          } else {
            setCcConnected(true);
            setCcStatus('connected');
          }
        } else {
          // Could not verify; trust the auth/status flag
          setCcConnected(true);
          setCcStatus('connected');
        }
      }
    } catch (error) {
      console.error('Error checking CC connection:', error);
    }
  };

  // Verifies the OAuth redirect URL actually reaches this API. If it does not,
  // Constant Contact's post-authorisation redirect lands on a 404 page.
  const runOAuthDiagnostics = async ({ silent = true } = {}) => {
    try {
      const res = await fetch(`${API_URL}/api/newsletter/oauth-diagnostics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return null;
      const diag = await res.json();

      let callbackReachable = null;
      let reachabilityDetail = '';
      try {
        // Probed server-side so an SPA/HTML fallback cannot masquerade as success
        const probe = await fetch(`${API_URL}/api/newsletter/callback-reachability`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (probe.ok) {
          const probeData = await probe.json();
          callbackReachable = probeData.reachable;
          reachabilityDetail = probeData.detail || '';
        }
      } catch (e) {
        callbackReachable = false;
      }
      const result = { ...diag, callbackReachable, reachabilityDetail };
      setOauthDiag(result);
      if (!silent && callbackReachable === false) {
        toast.error('The Constant Contact redirect URL is not reachable — authorising will end on a 404 page.');
      }
      return result;
    } catch (e) {
      return null;
    }
  };

  const handleConnectCC = async () => {
    // Catch the redirect-URI 404 before sending the user to Constant Contact
    const diag = await runOAuthDiagnostics({ silent: false });
    if (diag && (diag.callbackReachable === false || diag.healthy === false)) {
      toast.error('Fix the redirect URL configuration below before reconnecting.');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/newsletter/auth/login`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to initiate connection');
      }
    } catch (error) {
      toast.error('Error connecting to Constant Contact');
    }
  };

  const handleSync = async () => {
    if (!ccConnected) {
      toast.error('Please connect to Constant Contact first');
      return;
    }
    
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchData();
      } else {
        const error = await response.json();
        const errorMsg = error.detail || 'Sync failed';
        
        // Check if it's a token expiration issue
        if (errorMsg.includes('expired') || errorMsg.includes('Token') || errorMsg.includes('re-authenticate')) {
          toast.error('Constant Contact token expired. Please re-connect.');
          setCcConnected(false);
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Error during sync - please try again');
    } finally {
      setSyncing(false);
    }
  };

  const handleImportFromCC = async () => {
    if (!ccConnected) {
      toast.error('Please connect to Constant Contact first');
      return;
    }
    setConfirmAction({ type: 'import' });
  };

  const performImportFromCC = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/import-from-cc`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Imported ${result.imported} contacts (${result.skipped} skipped, ${result.errors} errors)`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Error during import');
    } finally {
      setImporting(false);
    }
  };

  const handleResetAllErrors = async () => {
    if (!stats?.sync_errors || stats.sync_errors === 0) {
      toast.info('No subscribers with sync errors to reset');
      return;
    }
    setConfirmAction({ type: 'reset_errors' });
  };

  const performResetAllErrors = async () => {
    setResettingErrors(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/reset-all-errors`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Reset failed');
      }
    } catch (error) {
      toast.error('Error resetting sync errors');
    } finally {
      setResettingErrors(false);
    }
  };

  const handleResetSingleSubscriber = async (subscriberId) => {
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribers/${subscriberId}/reset-sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.sync_status === 'synced') {
          toast.success('Subscriber synced to Constant Contact!');
        } else {
          toast.success('Subscriber reset for retry');
        }
        fetchData();
      } else {
        toast.error(data.detail || 'Sync failed - check error message');
        fetchData(); // Refresh to show the error
      }
    } catch (error) {
      toast.error('Error syncing subscriber');
      fetchData();
    }
  };

  const handleUpdateSubscriber = async () => {
    if (!editingSubscriber) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribers/${editingSubscriber.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      
      if (response.ok) {
        toast.success('Subscriber updated');
        setEditingSubscriber(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Update failed');
      }
    } catch (error) {
      toast.error('Error updating subscriber');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubscriber = async (id) => {
    const sub = subscribers.find(s => s.id === id);
    setDeleteSubscriberTarget(sub || { id });
  };

  const confirmDeleteSubscriber = async () => {
    if (!deleteSubscriberTarget) return;
    const id = deleteSubscriberTarget.id;
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Subscriber deleted');
        fetchData();
      } else {
        toast.error('Failed to delete subscriber');
      }
    } catch (error) {
      toast.error('Error deleting subscriber');
    } finally {
      setDeleteSubscriberTarget(null);
    }
  };

  const openEditModal = (subscriber) => {
    setEditingSubscriber(subscriber);
    setEditForm({
      first_name: subscriber.first_name,
      last_name: subscriber.last_name,
      member_type: subscriber.member_type,
      program_interest: subscriber.program_interest,
      newsletter_opt_in: subscriber.newsletter_opt_in
    });
  };

  // Filter subscribers
  const filteredSubscribers = subscribers.filter(sub => {
    if (filters.member_type && sub.member_type !== filters.member_type) return false;
    if (filters.program_interest && !sub.program_interest.includes(filters.program_interest)) return false;
    if (filters.newsletter_opt_in !== '' && sub.newsletter_opt_in !== (filters.newsletter_opt_in === 'true')) return false;
    if (filters.sync_status && sub.sync_status !== filters.sync_status) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (!sub.email.toLowerCase().includes(search) && 
          !sub.first_name.toLowerCase().includes(search) && 
          !sub.last_name.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return null; // AdminLayout handles loading
  }

  return (
    <AdminLayout title="Newsletter Subscribers" subtitle="Manage subscribers and Constant Contact sync">
      <div data-testid="admin-subscribers-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-stone-900">{stats.total_subscribers}</div>
                <div className="text-sm text-stone-500">Total Subscribers</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-green-600">{stats.opted_in}</div>
                <div className="text-sm text-stone-500">Opted In</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-stone-400">{stats.opted_out}</div>
                <div className="text-sm text-stone-500">Not Opted In</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-yellow-600">{stats.pending_sync}</div>
                <div className="text-sm text-stone-500">Pending Sync</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-cafa-blue">{stats.synced}</div>
                <div className="text-sm text-stone-500">Synced to CC</div>
              </CardContent>
            </Card>
            <Card className={stats.sync_errors > 0 ? 'border-red-200 bg-red-50' : ''}>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-red-600">{stats.sync_errors}</div>
                <div className="text-sm text-stone-500">Sync Errors</div>
                {stats.sync_errors > 0 && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="mt-2 text-xs border-red-300 text-red-600 hover:bg-red-100"
                    onClick={handleResetAllErrors}
                    disabled={resettingErrors}
                  >
                    {resettingErrors ? 'Resetting...' : 'Reset All for Retry'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Constant Contact Connection */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    ccStatus === 'connected' ? 'bg-green-500'
                    : ccStatus === 'expired' ? 'bg-amber-500'
                    : 'bg-red-500'
                  }`}
                />
                <div>
                  <h3 className="font-medium text-stone-900">Constant Contact Integration</h3>
                  <p className="text-sm text-stone-500" data-testid="cc-status-text">
                    {ccStatus === 'connected' && (
                      ccDetails?.expires_at
                        ? `Connected${ccDetails.account_name ? ` to ${ccDetails.account_name}` : ''} — token valid until ${new Date(ccDetails.expires_at).toLocaleString()}.`
                        : 'Connected and ready to sync.'
                    )}
                    {ccStatus === 'expired' && (ccDetails?.message || 'Connection expired — please reconnect Constant Contact.')}
                    {ccStatus === 'disconnected' && 'Not connected — click to authorize.'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="ghost" size="sm" onClick={() => runOAuthDiagnostics({ silent: false })} data-testid="cc-diagnostics-btn" title="Check the OAuth redirect URL configuration">
                  <AlertTriangle size={15} className="mr-1" /> Check Setup
                </Button>
                {ccStatus !== 'connected' ? (
                  <Button onClick={handleConnectCC} className="bg-cafa-blue hover:bg-cafa-blue-900" data-testid="cc-connect-btn">
                    <LinkIcon size={16} className="mr-1" />
                    {ccStatus === 'expired' ? 'Reconnect Constant Contact' : 'Connect to Constant Contact'}
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={handleConnectCC}>
                      <RefreshCw size={16} className="mr-1" /> Reconnect
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleImportFromCC} 
                      disabled={importing}
                      className="border-cafa-blue text-cafa-blue hover:bg-cafa-blue-50"
                      title="Pull contacts from Constant Contact into Emergent. Existing subscribers are skipped."
                    >
                      {importing ? (
                        <><RefreshCw size={16} className="mr-1 animate-spin" /> Importing...</>
                      ) : (
                        <><Download size={16} className="mr-1" /> Import from CC</>
                      )}
                    </Button>
                    <Button 
                      onClick={handleSync} 
                      disabled={syncing}
                      className="bg-cafa-red hover:bg-cafa-red-900"
                      title="Push pending Emergent subscribers up to Constant Contact."
                    >
                      {syncing ? (
                        <><RefreshCw size={16} className="mr-1 animate-spin" /> Syncing...</>
                      ) : (
                        <><Upload size={16} className="mr-1" /> Sync to CC</>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
            {oauthDiag && (
              <div
                className={`mt-3 rounded p-3 text-xs border ${
                  oauthDiag.callbackReachable === false || oauthDiag.healthy === false
                    ? 'bg-red-50 border-red-300 text-red-900'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                }`}
                data-testid="cc-oauth-diagnostics"
              >
                <p className="font-semibold mb-1">
                  {oauthDiag.callbackReachable === false || oauthDiag.healthy === false
                    ? 'OAuth setup problem — reconnecting will fail'
                    : 'OAuth setup looks correct'}
                </p>
                <p className="break-all"><strong>Redirect URL:</strong> {oauthDiag.redirect_uri || '(not set)'}</p>
                <p>
                  <strong>Redirect URL reachable:</strong>{' '}
                  {oauthDiag.callbackReachable === null ? 'not checked'
                    : oauthDiag.callbackReachable ? 'yes'
                    : 'NO'}
                </p>
                {oauthDiag.reachabilityDetail && (
                  <p className="mt-0.5">{oauthDiag.reachabilityDetail}</p>
                )}
                <p><strong>Scopes requested:</strong> {(oauthDiag.scopes || []).join(', ')}</p>
                <p><strong>Client ID in use:</strong> {oauthDiag.client_id_preview || '(not set)'} · secret {oauthDiag.client_secret_set ? 'set' : 'MISSING'}</p>
                {oauthDiag.client_id_looks_valid === false && (
                  <p className="mt-0.5">
                    That value is not a real Constant Contact Client ID — set the real one from
                    developer.constantcontact.com in your secrets.
                  </p>
                )}
                {(oauthDiag.issues || []).length > 0 && (
                  <ul className="mt-1 list-disc list-inside">
                    {oauthDiag.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                  </ul>
                )}
                <p className="mt-1 opacity-80">
                  This redirect URL must be registered character-for-character on your app at
                  developer.constantcontact.com.
                </p>
              </div>
            )}
            {ccDetails?.last_error && (
              <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900" data-testid="cc-last-error">
                <strong>Last token issue:</strong> {ccDetails.last_error}
              </div>
            )}
            {ccStatus === 'connected' && (
              <div className="mt-3 flex items-start gap-2 text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded p-2">
                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5 text-amber-600" />
                <span>
                  <strong>Import from CC</strong> reads contacts from Constant Contact into Emergent (existing subscribers are skipped, no overwrites). <strong>Sync to CC</strong> sends pending Emergent subscribers up to Constant Contact. Both actions write to your live Constant Contact account — review carefully before clicking.
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <MemberNewsletterCard
          token={token}
          ccConnected={ccStatus === 'connected'}
          onQueued={fetchData}
        />

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-stone-500">Search</Label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    placeholder="Search by email or name..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="w-40">
                <Label className="text-xs text-stone-500">Member Type</Label>
                <Select value={filters.member_type || "all"} onValueChange={(v) => setFilters({...filters, member_type: v === "all" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {MEMBER_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs text-stone-500">Interest</Label>
                <Select value={filters.program_interest || "all"} onValueChange={(v) => setFilters({...filters, program_interest: v === "all" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Interests" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Interests</SelectItem>
                    {PROGRAM_INTERESTS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <Label className="text-xs text-stone-500">Opt-In Status</Label>
                <Select value={filters.newsletter_opt_in || "all"} onValueChange={(v) => setFilters({...filters, newsletter_opt_in: v === "all" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Opted In</SelectItem>
                    <SelectItem value="false">Not Opted In</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <Label className="text-xs text-stone-500">Sync Status</Label>
                <Select value={filters.sync_status || "all"} onValueChange={(v) => setFilters({...filters, sync_status: v === "all" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="synced">Synced</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => setFilters({ member_type: '', program_interest: '', newsletter_opt_in: '', sync_status: '', search: '' })}>
                <X size={16} className="mr-1" /> Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Subscribers Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Subscribers ({filteredSubscribers.length})
              </CardTitle>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw size={14} className="mr-1" /> Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-y border-stone-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Interests</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-stone-500 uppercase">Opt-In</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Sync Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-stone-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredSubscribers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-stone-500">
                        <Users size={32} className="mx-auto mb-2 text-stone-300" />
                        No subscribers found
                      </td>
                    </tr>
                  ) : (
                    filteredSubscribers.map(sub => (
                      <tr key={sub.id} className="hover:bg-stone-50">
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-stone-900">{sub.email}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-stone-600">
                            {sub.first_name} {sub.last_name}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-stone-100 text-stone-700 px-2 py-1 rounded capitalize">
                            {sub.member_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {sub.program_interest.map(interest => (
                              <span key={interest} className="text-xs bg-cafa-blue-100 text-cafa-blue-800 px-2 py-0.5 rounded">
                                {interest.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {sub.newsletter_opt_in ? (
                            <Check size={18} className="inline text-green-600" />
                          ) : (
                            <X size={18} className="inline text-stone-400" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-1 rounded ${SYNC_STATUS_COLORS[sub.sync_status] || SYNC_STATUS_COLORS.none}`}>
                                {sub.sync_status || 'none'}
                              </span>
                              {sub.sync_status === 'error' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 px-2 text-xs text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                                  onClick={() => handleResetSingleSubscriber(sub.id)}
                                  title={sub.sync_error || 'Click to retry sync'}
                                >
                                  <RefreshCw size={12} className="mr-1" />
                                  Retry
                                </Button>
                              )}
                            </div>
                            {sub.sync_status === 'error' && sub.sync_error && (
                              <p className="text-xs text-red-500 mt-1 max-w-xs truncate" title={sub.sync_error}>
                                {sub.sync_error}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-stone-500 capitalize">
                            {sub.opt_in_source?.replace('_', ' ') || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" aria-label={`Edit subscriber ${sub.email}`} title="Edit subscriber" onClick={() => openEditModal(sub)}>
                              <Edit2 size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" aria-label={`Delete subscriber ${sub.email}`} title="Delete subscriber" onClick={() => handleDeleteSubscriber(sub.id)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Modal */}
      <Dialog open={!!editingSubscriber} onOpenChange={(open) => !open && setEditingSubscriber(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Subscriber</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Email</Label>
              <Input value={editingSubscriber?.email || ''} disabled className="bg-stone-100" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input
                  value={editForm.first_name || ''}
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={editForm.last_name || ''}
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Member Type</Label>
              <Select value={editForm.member_type} onValueChange={(v) => setEditForm({...editForm, member_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEMBER_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Newsletter Opt-In</Label>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={editForm.newsletter_opt_in || false}
                  onChange={(e) => setEditForm({...editForm, newsletter_opt_in: e.target.checked})}
                  className="w-4 h-4 rounded border-stone-300"
                />
                <span className="text-sm text-stone-600">
                  {editForm.newsletter_opt_in ? 'Subscribed to newsletter' : 'Not subscribed'}
                </span>
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleUpdateSubscriber} disabled={saving} className="flex-1 bg-cafa-red hover:bg-cafa-red-900">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={() => setEditingSubscriber(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Subscriber Confirmation */}
      <AlertDialog open={!!deleteSubscriberTarget} onOpenChange={(open) => { if (!open) setDeleteSubscriberTarget(null); }}>
        <AlertDialogContent data-testid="delete-subscriber-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="text-red-600" size={20} />
              Delete this subscriber?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-stone-700 space-y-2">
              <span className="block">
                You are about to delete <strong>{deleteSubscriberTarget?.email || 'this subscriber'}</strong> from the Emergent newsletter list.
              </span>
              <span className="block text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                This <strong>cannot be undone</strong>. The contact may still exist in Constant Contact and will need to be removed there separately if you want a full unsubscribe.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-subscriber-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDeleteSubscriber}
              data-testid="delete-subscriber-confirm"
            >
              <Trash2 size={14} className="mr-2" />
              Yes, Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import / Reset Errors Confirmation */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent data-testid="cc-confirm-dialog">
          {confirmAction?.type === 'import' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-cafa-blue">
                  <Download size={20} />
                  Import contacts from Constant Contact?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-base text-stone-700 space-y-2">
                  <span className="block">
                    This will pull all contacts from your Constant Contact account into the Emergent subscriber list.
                  </span>
                  <span className="block text-sm bg-blue-50 border border-blue-200 rounded p-2 text-blue-900">
                    Existing Emergent subscribers (matched by email) will be <strong>skipped</strong>, not overwritten. Imported records are read-only copies — your Constant Contact data is not modified.
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-cafa-blue hover:bg-cafa-blue-900 text-white"
                  onClick={() => { performImportFromCC(); setConfirmAction(null); }}
                  data-testid="import-confirm-btn"
                >
                  <Download size={14} className="mr-2" />
                  Import Contacts
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {confirmAction?.type === 'reset_errors' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle size={20} className="text-amber-600" />
                  Reset {stats?.sync_errors} sync error{stats?.sync_errors === 1 ? '' : 's'}?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-base text-stone-700 space-y-2">
                  <span className="block">
                    All subscribers currently in <strong>error</strong> state will be reset to <strong>pending</strong>.
                  </span>
                  <span className="block text-sm bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
                    They will be retried automatically on the next "Sync to CC". This does not delete any records or contact your subscribers — it only changes their internal sync flag.
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => { performResetAllErrors(); setConfirmAction(null); }}
                  data-testid="reset-errors-confirm-btn"
                >
                  <RefreshCw size={14} className="mr-2" />
                  Reset for Retry
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSubscribers;
